/**
 * Docked admin assistant, ported from the CCA prototype and wired to
 * assistant-service. Chips cover the one supported scenario (sign-in problems).
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MarkdownRenderer,
  SendIcon,
  ThreeDotIcon,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import colors from '@/styles/colors';
import theme from '@/styles/theme';

import useAssistantChat from '../../hooks/useAssistantChat';

import ToolProgress from './ToolProgress';
import TypingDots from './TypingDots';

const DOCK_WIDTH = 384;

const GREETING =
  'I can help with a participant who cannot sign in — I can check their login ' +
  'history, whether the account is locked, and verify identity details the caller ' +
  'gives you. I read data only; I cannot unlock accounts or change contact details.';

/** Quick asks — sent verbatim, prefixed with the participant's name. */
const QUICK_ASKS = [
  {
    id: 'cannot-sign-in',
    label: 'Caller cannot sign in',
    text: 'The caller says they cannot sign in. What is going on with this account?',
  },
  { id: 'locked', label: 'Is the account locked?', text: 'Is this account locked, and what locked it?' },
  {
    id: 'recent-logins',
    label: 'Recent login activity',
    text: 'Summarise the login attempts on this account over the last 7 days.',
  },
  {
    id: 'takeover',
    label: 'Takeover risk?',
    text: 'Does the recent login activity look like an attempted account takeover?',
  },
];

const Dock = styled.div<{ $expanded: boolean }>`
  position: fixed;
  right: 24px;
  bottom: 0;
  z-index: 40;
  width: ${DOCK_WIDTH}px;
  max-width: calc(100vw - 48px);
  display: flex;
  flex-direction: column;
  background: ${colors.canvas};
  border: none;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 12px 40px rgba(15, 22, 33, 0.18);
  height: ${({ $expanded }) => ($expanded ? 'min(790px, calc(100vh - 96px))' : '56px')};
  transition: height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  flex-shrink: 0;
  background: ${colors.navigationBackground};
  border-bottom: 1px solid ${colors.navigationBorder};
  user-select: none;
`;

const HeadTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: ${colors.navigationTextDefault};
`;

const HeadIcons = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const IconBtn = styled.button`
  border: none;
  background: transparent;
  color: ${colors.navigationIconDefault};
  border-radius: 8px;
  padding: 6px;
  cursor: pointer;
  display: flex;
  &:hover {
    background: ${colors.navigationAccentHover};
  }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AiRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const AiAvatar = styled.div`
  flex-shrink: 0;
  align-self: flex-start;
  height: 20px;
  padding: 0 8px;
  border-radius: 6px;
  background: ${colors.module};
  color: ${colors.textSecondary};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Bubble = styled.div<{ $error?: boolean }>`
  background: ${colors.module};
  border: 1px solid ${({ $error }) => ($error ? colors.border1 : 'transparent')};
  border-radius 12px:
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ $error }) => ($error ? colors.textSecondary : colors.textBody)};
  width: 100%;
`;

const UserRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const UserBubble = styled.div`
  background: ${colors.accentActive};
  color: ${colors.textOnColor};
  border-radius: 12px 12px 4px 12px;
  padding: 10px 14px;
  font-size: 14px;
  max-width: 80%;
`;

const SubHead = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button`
  border: 1px solid ${colors.accentBorder};
  background: ${colors.accentContainer};
  color: ${colors.accentActive};
  font-weight: 600;
  font-size: 13px;
  border-radius: 999px;
  padding: 7px 12px;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  &:not(:disabled):hover {
    background: ${colors.buttonTertiaryHover};
  }
`;

const Footer = styled.div`
  border-top: 1px solid ${colors.border1};
  padding: 12px;
  flex-shrink: 0;
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  border: 1px solid ${colors.border1};
  border-radius: ${theme.rounds.container1Round};
  padding: 8px 10px;
  textarea {
    flex: 1;
    border: none;
    outline: none;
    resize: none;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    max-height: 140px;
    padding: 6px 0;
    background: transparent;
    color: ${colors.textBody};
    overflow-y: auto;
  }
`;

const SendBtn = styled.button<{ $enabled: boolean }>`
  border: none;
  background: ${({ $enabled }) => ($enabled ? colors.buttonPrimary : colors.border1)};
  color: ${colors.textOnColor};
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $enabled }) => ($enabled ? 'pointer' : 'default')};
`;

const MenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 45;
`;

const HeaderMenu = styled.div`
  position: fixed;
  z-index: 46;
  width: 190px;
  background: ${colors.canvas};
  border: 1px solid ${colors.border1};
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 22, 33, 0.2);
  padding: 4px;
`;

const MenuItem = styled.button`
  display: block;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  white-space: nowrap;
  font-size: 14px;
  color: ${colors.textBody};
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    background: ${colors.module};
  }
`;

interface Props {
  /** Full name of the participant whose page is open. */
  participantName: string;
}

const AssistantWidget: React.FC<Props> = ({ participantName }) => {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const draftRef = useRef<HTMLTextAreaElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const kebabRef = useRef<HTMLButtonElement>(null);

  const { messages, busy, send, reset } = useAssistantChat({ participantName });

  // Auto-grow the composer up to the CSS max-height, then let it scroll.
  useEffect(() => {
    const el = draftRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  useEffect(() => {
    if (expanded && bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, expanded]);

  const sendFreeText = () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    void send(text);
  };

  const menuStyle: React.CSSProperties = (() => {
    const r = kebabRef.current?.getBoundingClientRect();
    if (!r) return {};
    return { left: Math.max(12, r.right - 190), bottom: window.innerHeight - r.top + 6 };
  })();

  return (
    <>
      <Dock $expanded={expanded} data-testid="assistant-dock">
        <Head onClick={() => setExpanded((v) => !v)} data-testid="assistant-header">
          <HeadTitle>Employee AI Assistant</HeadTitle>
          <HeadIcons>
            <IconBtn
              ref={kebabRef}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
              title="Menu"
              data-testid="assistant-menu-button"
            >
              <ThreeDotIcon size="18px" color="navigationIconDefault" />
            </IconBtn>
            <IconBtn as="span" aria-hidden style={{ pointerEvents: 'none' }}>
              {expanded ? (
                <ChevronDownIcon size="20px" color="navigationIconDefault" />
              ) : (
                <ChevronUpIcon size="20px" color="navigationIconDefault" />
              )}
            </IconBtn>
          </HeadIcons>
        </Head>

        {expanded && (
          <>
            <Body ref={bodyRef} data-testid="assistant-panel">
              <AiRow>
                <AiAvatar>AI</AiAvatar>
                <Bubble>{GREETING}</Bubble>
              </AiRow>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <SubHead>Quick asks</SubHead>
                <ChipRow>
                  {QUICK_ASKS.map((q) => (
                    <Chip
                      key={q.id}
                      disabled={busy}
                      onClick={() => void send(q.text)}
                      data-testid={`quick-ask-${q.id}`}
                    >
                      {q.label}
                    </Chip>
                  ))}
                </ChipRow>
              </div>

              {messages.map((m) =>
                m.role === 'user' ? (
                  <UserRow key={m.id}>
                    <UserBubble>{m.text}</UserBubble>
                  </UserRow>
                ) : (
                  <AiRow key={m.id}>
                    <AiAvatar>AI</AiAvatar>
                    <div style={{ width: '100%' }}>
                      <Bubble $error={m.error}>
                        {m.tools && m.tools.length > 0 && <ToolProgress tools={m.tools} />}
                        {m.pending && !m.text && <TypingDots />}
                        {m.error ? m.text : <MarkdownRenderer text={m.text} stripEmoji />}
                      </Bubble>
                    </div>
                  </AiRow>
                )
              )}
            </Body>

            <Footer>
              <InputRow>
                <textarea
                  ref={draftRef}
                  rows={1}
                  placeholder={busy ? 'Assistant is replying…' : 'Ask about this employee…'}
                  value={draft}
                  disabled={busy}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendFreeText();
                    }
                  }}
                  data-testid="assistant-input"
                />
                <SendBtn
                  $enabled={!!draft.trim() && !busy}
                  onClick={sendFreeText}
                  data-testid="assistant-send"
                >
                  <SendIcon size="16px" color="textOnColor" />
                </SendBtn>
              </InputRow>
            </Footer>
          </>
        )}
      </Dock>

      {menuOpen && (
        <>
          <MenuBackdrop onClick={() => setMenuOpen(false)} />
          <HeaderMenu style={menuStyle} data-testid="assistant-menu">
            <MenuItem
              onClick={() => {
                void reset();
                setMenuOpen(false);
              }}
            >
              Reset chat
            </MenuItem>
          </HeaderMenu>
        </>
      )}
    </>
  );
};

export default AssistantWidget;
